import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Chessboard } from 'react-chessboard';
import { Chess } from 'chess.js';
import { Puzzle, RotateCcw, Eye, CheckCircle, XCircle, ExternalLink } from 'lucide-react';
import useBlockStyles from '../common/useBlockStyles';

const STYLE_OPTIONS = {
  defaultBackground: 'section',
  defaultHeadingSize: 'medium',
  defaultAlignment: 'center',
  defaultPadding: 'normal',
};

const CACHE_KEY_PREFIX = 'lichess-daily-puzzle';

function getCacheKey() {
  const d = new Date();
  return `${CACHE_KEY_PREFIX}-${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

function getCachedPuzzle() {
  try {
    const raw = localStorage.getItem(getCacheKey());
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function cachePuzzle(data) {
  try {
    // Clear old cache entries
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(CACHE_KEY_PREFIX) && key !== getCacheKey()) {
        localStorage.removeItem(key);
      }
    }
    localStorage.setItem(getCacheKey(), JSON.stringify(data));
  } catch {
    // localStorage full or unavailable — no big deal
  }
}

/**
 * Parse the Lichess daily puzzle response into a playable puzzle.
 *
 * The API returns:
 * - game.pgn: space-separated SAN moves (no move numbers or headers)
 * - puzzle.initialPly: metadata — which ply in the game the puzzle starts at
 * - puzzle.solution: array of UCI moves (e.g. ["g4g6", "e5h5", ...])
 *
 * Flow:
 * 1. Play ALL PGN moves to reach the puzzle starting position
 * 2. The last PGN move is the "blunder" / setup move (highlighted on board)
 * 3. solution[0] is the player's FIRST move to find
 * 4. solution[1] is the opponent's auto-response
 * 5. solution[2] is the player's second move, etc.
 *
 * Player moves = even indices (0, 2, 4, ...)
 * Opponent auto-plays = odd indices (1, 3, 5, ...)
 */
function parsePuzzle(data) {
  const chess = new Chess();
  const pgn = data.game.pgn;
  const pgnMoves = pgn.split(/\s+/).filter(Boolean);

  // Play ALL PGN moves to reach the puzzle position
  let lastPgnMove = null;
  for (const san of pgnMoves) {
    lastPgnMove = chess.move(san);
  }

  const puzzleFen = chess.fen();
  const playerColor = chess.turn() === 'w' ? 'white' : 'black';

  // Convert all solution UCI moves to SAN
  const solutionSan = [];
  const verify = new Chess(puzzleFen);
  for (const uci of data.puzzle.solution) {
    const m = verify.move({
      from: uci.slice(0, 2),
      to: uci.slice(2, 4),
      promotion: uci.length > 4 ? uci[4] : undefined,
    });
    if (m) solutionSan.push(m.san);
  }

  return {
    puzzleId: data.puzzle.id,
    rating: data.puzzle.rating,
    themes: data.puzzle.themes || [],
    puzzleFen,
    playerColor,
    solutionSan,
    setupMove: lastPgnMove ? { from: lastPgnMove.from, to: lastPgnMove.to } : null,
    gameUrl: `https://lichess.org/training/${data.puzzle.id}`,
  };
}

const DailyPuzzleRenderer = ({ block, theme = {} }) => {
  const content = block.content || {};
  const styles = useBlockStyles(content, theme, STYLE_OPTIONS);

  const [puzzleData, setPuzzleData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [game, setGame] = useState(null);
  const [moveIndex, setMoveIndex] = useState(0);
  const [status, setStatus] = useState('playing'); // playing | wrong | correct | solved
  const [wrongSquare, setWrongSquare] = useState(null);
  const [selectedSquare, setSelectedSquare] = useState(null);
  const [showSolution, setShowSolution] = useState(false);
  const boardRef = useRef(null);

  // Fetch puzzle
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      // Check cache first (validate it has required fields)
      const cached = getCachedPuzzle();
      if (cached?.puzzleFen && cached?.solutionSan?.length > 0) {
        if (!cancelled) {
          setPuzzleData(cached);
          setLoading(false);
        }
        return;
      }

      try {
        const res = await fetch('https://lichess.org/api/puzzle/daily', {
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) throw new Error(`Lichess API error: ${res.status}`);
        const data = await res.json();
        const parsed = parsePuzzle(data);
        cachePuzzle(parsed);
        if (!cancelled) {
          setPuzzleData(parsed);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError('Could not load today\'s puzzle. Try refreshing.');
          setLoading(false);
        }
      }
    };

    load();
    return () => { cancelled = true; };
  }, []);

  // Initialize game from puzzle data
  useEffect(() => {
    if (puzzleData?.puzzleFen) {
      setGame(new Chess(puzzleData.puzzleFen));
      setMoveIndex(0);
      setStatus('playing');
      setWrongSquare(null);
      setSelectedSquare(null);
      setShowSolution(false);
    }
  }, [puzzleData]);

  const solutionMoves = puzzleData?.solutionSan || [];
  const expectedMove = solutionMoves[moveIndex] || null;

  const safeAccent = styles.accentColor || '#749555';
  const matchTheme = content.matchThemeSquares === true;

  // Get legal moves for selected square (for move dots)
  const legalMovesForSelected = useMemo(() => {
    if (!game || !selectedSquare) return [];
    try {
      return game.moves({ square: selectedSquare, verbose: true });
    } catch {
      return [];
    }
  }, [game, selectedSquare]);

  const onDrop = useCallback((sourceSquare, targetSquare) => {
    if (!game || status === 'solved' || !expectedMove) return false;

    const gameCopy = new Chess(game.fen());
    let move;
    try {
      move = gameCopy.move({
        from: sourceSquare,
        to: targetSquare,
        promotion: 'q',
      });
    } catch {
      return false;
    }
    if (!move) return false;

    if (move.san === expectedMove) {
      // Correct move
      setGame(gameCopy);
      setSelectedSquare(null);
      setWrongSquare(null);
      const nextIndex = moveIndex + 1;
      setMoveIndex(nextIndex);

      if (nextIndex >= solutionMoves.length) {
        setStatus('solved');
      } else {
        setStatus('correct');
        // Auto-play opponent response after brief delay
        setTimeout(() => {
          const opponentSan = solutionMoves[nextIndex];
          const afterOpponent = new Chess(gameCopy.fen());
          try {
            afterOpponent.move(opponentSan);
            setGame(afterOpponent);
            setMoveIndex(nextIndex + 1);
            setStatus('playing');
          } catch {
            setStatus('solved');
          }
        }, 400);
      }
      return true;
    } else {
      // Wrong move
      setWrongSquare(targetSquare);
      setStatus('wrong');
      setSelectedSquare(null);
      setTimeout(() => {
        setWrongSquare(null);
        setStatus('playing');
      }, 800);
      return false;
    }
  }, [game, status, expectedMove, moveIndex, solutionMoves]);

  const onSquareClick = useCallback((square) => {
    if (!game || status === 'solved') return;

    if (!selectedSquare) {
      // Select a piece if it belongs to the player
      const piece = game.get(square);
      const playerChar = puzzleData?.playerColor === 'white' ? 'w' : 'b';
      if (piece && piece.color === playerChar) {
        setSelectedSquare(square);
      }
    } else {
      if (square === selectedSquare) {
        setSelectedSquare(null);
      } else {
        // Check if clicking another own piece — switch selection
        const piece = game.get(square);
        const playerChar = puzzleData?.playerColor === 'white' ? 'w' : 'b';
        if (piece && piece.color === playerChar) {
          setSelectedSquare(square);
        } else {
          onDrop(selectedSquare, square);
          setSelectedSquare(null);
        }
      }
    }
  }, [game, status, selectedSquare, puzzleData?.playerColor, onDrop]);

  const handleReset = useCallback(() => {
    if (puzzleData?.puzzleFen) {
      setGame(new Chess(puzzleData.puzzleFen));
      setMoveIndex(0);
      setStatus('playing');
      setWrongSquare(null);
      setSelectedSquare(null);
      setShowSolution(false);
    }
  }, [puzzleData]);

  const customSquareStyles = useMemo(() => {
    const sq = {};

    // Last setup move highlight
    if (puzzleData?.setupMove && moveIndex === 0) {
      const highlight = 'rgba(255, 170, 0, 0.35)';
      sq[puzzleData.setupMove.from] = { backgroundColor: highlight };
      sq[puzzleData.setupMove.to] = { backgroundColor: highlight };
    }

    // Wrong move highlight
    if (wrongSquare) {
      sq[wrongSquare] = {
        backgroundColor: 'rgba(239, 68, 68, 0.5)',
        borderRadius: '50%',
      };
    }

    // Selected square
    if (selectedSquare) {
      sq[selectedSquare] = {
        backgroundColor: `${safeAccent}99`,
        borderRadius: '4px',
      };
    }

    // Legal move dots
    if (selectedSquare && legalMovesForSelected.length > 0) {
      for (const m of legalMovesForSelected) {
        const isCapture = game && game.get(m.to);
        if (m.to === selectedSquare) continue;
        sq[m.to] = {
          background: isCapture
            ? `radial-gradient(circle, transparent 55%, ${safeAccent}80 56%)`
            : `radial-gradient(circle, ${safeAccent}40 22%, transparent 23%)`,
          borderRadius: '50%',
        };
      }
    }

    return sq;
  }, [wrongSquare, selectedSquare, legalMovesForSelected, puzzleData, moveIndex, safeAccent, game]);

  // --- Render states ---

  if (loading) {
    return (
      <div style={{ background: styles.sectionBackground }}>
        <div style={{ padding: '80px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', color: styles.mutedColor }}>
          <div className="dp-spinner" />
          <span style={{ marginTop: 16 }}>Loading today's puzzle...</span>
          <DailyPuzzleStyles accent={safeAccent} />
        </div>
      </div>
    );
  }

  if (error || !puzzleData) {
    return (
      <div style={{ background: styles.sectionBackground }}>
        <div style={{ padding: '60px 24px', textAlign: 'center', color: styles.mutedColor }}>
          <Puzzle size={48} style={{ marginBottom: 16, opacity: 0.4 }} />
          <p>{error || 'Puzzle unavailable'}</p>
          <DailyPuzzleStyles accent={safeAccent} />
        </div>
      </div>
    );
  }

  const playerLabel = puzzleData.playerColor === 'white' ? 'White' : 'Black';
  const playerMoveCount = Math.ceil(solutionMoves.length / 2);
  const currentPlayerMove = Math.ceil((moveIndex + 1) / 2);

  return (
    <div className="dp-outer" style={{ background: styles.sectionBackground }}>
      <div className="dp-container">
        {/* Two-column layout: info LEFT, board RIGHT (opposite of LiveChessGame) */}
        <div className="dp-layout">
          {/* LEFT COLUMN: Title (desktop) + Info panel */}
          <div className="dp-left">
            <h2 className="dp-title dp-title-desktop" style={{ color: styles.headingColor }}>
              {content.title || 'Daily Puzzle'}
            </h2>
            {/* Primary info card — matches lcg-info-card style */}
            <div className="dp-info-card">
              <div className={`dp-turn ${puzzleData.playerColor}`}>
                <div className={`dp-turn-dot ${puzzleData.playerColor}`} />
                {playerLabel} to move
              </div>

              <span className="dp-rating">Rating {puzzleData.rating}</span>

              {/* Status */}
              <div className="dp-status">
                {status === 'solved' && (
                  <div className="dp-status-msg solved">
                    <CheckCircle size={20} />
                    Puzzle solved!
                  </div>
                )}
                {status === 'wrong' && (
                  <div className="dp-status-msg wrong">
                    <XCircle size={20} />
                    Not quite — try again.
                  </div>
                )}
                {status === 'correct' && (
                  <div className="dp-status-msg correct">
                    <CheckCircle size={20} />
                    Correct! Keep going...
                  </div>
                )}
                {status === 'playing' && moveIndex === 0 && (
                  <div className="dp-status-msg progress" style={{ color: styles.mutedColor }}>
                    Find the best move
                  </div>
                )}
                {status === 'playing' && moveIndex > 0 && moveIndex < solutionMoves.length && (
                  <div className="dp-status-msg progress" style={{ color: styles.mutedColor }}>
                    Move {currentPlayerMove} of {playerMoveCount}
                  </div>
                )}
              </div>
            </div>

            {/* Themes card — matches lcg-pgn style */}
            {puzzleData.themes.length > 0 && (
              <div className="dp-themes-card">
                <div className="dp-themes">
                  {puzzleData.themes.map((t) => (
                    <span key={t} className="dp-theme-tag" style={{ color: styles.mutedColor }}>
                      {t.replace(/([A-Z])/g, ' $1').trim()}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="dp-actions">
              <button
                className="dp-btn reset"
                onClick={handleReset}
                style={{ borderColor: styles.mutedColor + '40', color: styles.bodyColor }}
              >
                <RotateCcw size={16} /> Reset
              </button>
              {status !== 'solved' && (
                <button
                  className="dp-btn solution"
                  onClick={() => setShowSolution(true)}
                  style={{ borderColor: styles.mutedColor + '40', color: styles.bodyColor }}
                >
                  <Eye size={16} /> Show Solution
                </button>
              )}
            </div>

            {/* Solution reveal */}
            {showSolution && (
              <div className="dp-solution">
                <Eye size={16} />
                <span>{solutionMoves.join(' ')}</span>
              </div>
            )}

            {/* Attribution */}
            <a
              href={puzzleData.gameUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="dp-attribution"
              style={{ color: styles.mutedColor }}
            >
              Puzzle from Lichess.org <ExternalLink size={12} />
            </a>
          </div>

          {/* RIGHT COLUMN: Board (title on mobile only) */}
          <div className="dp-right">
            <h2 className="dp-title dp-title-mobile" style={{ color: styles.headingColor }}>
              {content.title || 'Daily Puzzle'}
            </h2>
            <div className="dp-board-area" ref={boardRef}>
              <Chessboard
                position={game?.fen() || puzzleData.puzzleFen}
                onPieceDrop={onDrop}
                onSquareClick={onSquareClick}
                boardOrientation={puzzleData.playerColor}
                customSquareStyles={customSquareStyles}
                animationDuration={200}
                arePiecesDraggable={status !== 'solved'}
                areArrowsAllowed={false}
                arePremovesAllowed={false}
                customLightSquareStyle={{ backgroundColor: '#eeeed2' }}
                customDarkSquareStyle={{
                  backgroundColor: matchTheme ? safeAccent : '#769656',
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <DailyPuzzleStyles accent={safeAccent} />
    </div>
  );
};

const DailyPuzzleStyles = ({ accent }) => (
  <style>{`
    .dp-outer {
      width: 100%;
    }

    .dp-container {
      max-width: 1200px;
      margin: 0 auto;
      padding: 40px 24px;
    }

    .dp-layout {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 32px;
      align-items: center;
    }

    /* LEFT COLUMN: Info panel */
    .dp-left {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    /* Primary info card — matches LiveChessGame lcg-info-card */
    .dp-info-card {
      background: rgba(255, 255, 255, 0.04);
      border: 1px solid rgba(255, 255, 255, 0.08);
      border-radius: 14px;
      padding: 24px;
      text-align: center;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;
    }

    .dp-turn {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      font-size: 0.95rem;
      font-weight: 700;
      padding: 6px 14px;
      border-radius: 8px;
      width: fit-content;
    }

    .dp-turn-dot {
      width: 12px;
      height: 12px;
      border-radius: 50%;
      flex-shrink: 0;
    }

    .dp-turn-dot.white {
      background: #ffffff;
      box-shadow: 0 0 0 1.5px rgba(0, 0, 0, 0.15);
    }

    .dp-turn-dot.black {
      background: #1a1a1a;
      box-shadow: 0 0 0 1.5px rgba(255, 255, 255, 0.15);
    }

    .dp-turn.white {
      background: rgba(255, 255, 255, 0.95);
      color: #1a1a1a;
    }

    .dp-turn.black {
      background: rgba(30, 30, 30, 0.9);
      color: #e5e5e5;
    }

    .dp-rating {
      font-size: 0.85rem;
      font-weight: 600;
      color: #9ca3af;
    }

    .dp-status {
      min-height: 32px;
      display: flex;
      justify-content: center;
    }

    .dp-status-msg {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 16px;
      border-radius: 8px;
      font-weight: 600;
      font-size: 0.9rem;
    }

    .dp-status-msg.solved {
      background: rgba(16, 185, 129, 0.15);
      color: #10b981;
    }

    .dp-status-msg.correct {
      background: rgba(16, 185, 129, 0.1);
      color: #10b981;
      animation: dpPulse 0.4s ease-out;
    }

    .dp-status-msg.wrong {
      background: rgba(239, 68, 68, 0.15);
      color: #ef4444;
      animation: dpShake 0.3s ease-in-out;
    }

    .dp-status-msg.progress {
      font-size: 0.85rem;
      font-weight: 500;
    }

    @keyframes dpShake {
      0%, 100% { transform: translateX(0); }
      25% { transform: translateX(-4px); }
      75% { transform: translateX(4px); }
    }

    @keyframes dpPulse {
      0% { transform: scale(1); }
      50% { transform: scale(1.05); }
      100% { transform: scale(1); }
    }

    /* Themes card — matches LiveChessGame lcg-pgn */
    .dp-themes-card {
      padding: 14px 18px;
      background: rgba(255, 255, 255, 0.04);
      border: 1px solid rgba(255, 255, 255, 0.08);
      border-radius: 14px;
      text-align: center;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .dp-themes {
      display: flex;
      gap: 6px;
      flex-wrap: wrap;
      justify-content: center;
    }

    .dp-theme-tag {
      font-size: 0.75rem;
      padding: 3px 10px;
      border-radius: 10px;
      background: rgba(128, 128, 128, 0.12);
      text-transform: capitalize;
    }

    /* Actions row */
    .dp-actions {
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
      justify-content: center;
    }

    .dp-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      padding: 10px 16px;
      border-radius: 10px;
      font-size: 0.85rem;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.15s;
      border: 1px solid;
      background: transparent;
      font-family: inherit;
      flex: 1;
      min-width: 120px;
    }

    .dp-btn:hover {
      border-color: ${accent};
      color: ${accent};
    }

    /* Solution card — matches themes card style */
    .dp-solution {
      display: flex;
      align-items: flex-start;
      gap: 10px;
      padding: 14px 18px;
      border-radius: 14px;
      font-size: 0.85rem;
      text-align: left;
      background: rgba(59, 130, 246, 0.06);
      border: 1px solid rgba(59, 130, 246, 0.15);
      color: #60a5fa;
      font-family: 'Monaco', 'Menlo', monospace;
      line-height: 1.6;
      word-break: break-word;
    }

    .dp-attribution {
      font-size: 0.75rem;
      opacity: 0.5;
      text-decoration: none;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 4px;
      transition: opacity 0.15s;
      width: 100%;
    }

    .dp-attribution:hover {
      opacity: 1;
    }

    /* RIGHT COLUMN: Title + Board */
    .dp-right {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 16px;
    }

    .dp-title {
      margin: 0;
      font-size: 1.75rem;
      font-weight: 700;
      text-align: center;
    }

    .dp-title-mobile {
      display: none;
    }

    .dp-title-desktop {
      display: block;
    }

    .dp-board-area {
      width: 100%;
      aspect-ratio: 1;
      max-width: 450px;
      overflow: hidden;
    }

    .dp-spinner {
      width: 40px;
      height: 40px;
      border: 3px solid ${accent}33;
      border-top-color: ${accent};
      border-radius: 50%;
      animation: dpSpin 1s linear infinite;
    }

    @keyframes dpSpin {
      to { transform: rotate(360deg); }
    }

    @media (max-width: 768px) {
      .dp-layout {
        grid-template-columns: 1fr;
        gap: 24px;
      }

      .dp-left {
        order: 2;
      }

      .dp-right {
        order: 1;
      }

      .dp-title-mobile {
        display: block;
      }

      .dp-title-desktop {
        display: none;
      }

      .dp-container {
        padding: 24px 16px;
      }

      .dp-title {
        font-size: 1.5rem;
      }

      .dp-board-area {
        max-width: 400px;
      }

      .dp-btn {
        flex: unset;
      }
    }

    @media (max-width: 480px) {
      .dp-title {
        font-size: 1.3rem;
      }

      .dp-board-area {
        max-width: calc(100vw - 48px);
      }
    }
  `}</style>
);

export default DailyPuzzleRenderer;
